import { useEffect, useState } from 'react';
import { resources } from '../i18n/i18n';
import { Button } from '../ui';

/**
 * Web: the database can be open in one tab at a time (OPFS). A second tab
 * shows this screen instead of running on a copy in memory that would be
 * lost; "Use it here" asks the other tab to hand the database over, and a
 * tab that hands it over shows that it moved. Tabs talk over a
 * BroadcastChannel; the handover itself is the worker's 'budget-db' lock.
 */
const CHANNEL = 'costline-db';
const TAKE_OVER = 'take-over';
/** The app language, kept outside the database so this screen can speak it before the database is open. */
const LANGUAGE_KEY = 'costline.language';

export function rememberLanguage(language: string) {
  try {
    localStorage.setItem(LANGUAGE_KEY, language);
  } catch {
    /* storage unavailable: the browser language is used */
  }
}

function copy() {
  let language = '';
  try {
    language = localStorage.getItem(LANGUAGE_KEY) ?? '';
  } catch {
    /* storage unavailable */
  }
  return ((language || navigator.language).toLowerCase().startsWith('tr') ? resources.tr : resources.en).translation.otherTab;
}

/** Ask the tab that has the database to let go of it. */
export function askForTheDatabase() {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(CHANNEL);
  channel.postMessage(TAKE_OVER);
  channel.close();
}

/** In the tab that has the database: when another tab asks, let go of it and show `onMoved`. */
export function handOverWhenAsked(release: () => Promise<void>, onMoved: () => void) {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (event: MessageEvent) => {
    if (event.data !== TAKE_OVER) return;
    channel.close();
    void release()
      .catch((e: unknown) => console.warn('[tabs] release', e))
      .finally(onMoved);
  };
}

/**
 * `moved`: this tab handed the database over. Otherwise another tab has it,
 * and this one opens by itself as soon as that tab closes or lets go.
 */
export function OtherTabScreen({ moved = false }: { moved?: boolean }) {
  const t = copy();
  const [asked, setAsked] = useState(false);

  useEffect(() => {
    if (!asked) return;
    // A tab that never answers (frozen in the background): let the person try again.
    const again = setTimeout(() => setAsked(false), 6000);
    return () => clearTimeout(again);
  }, [asked]);

  const useHere = () => {
    askForTheDatabase();
    // This page no longer has an app to return to: start again, the other tab is letting go.
    if (moved) window.location.reload();
    else setAsked(true);
  };

  return (
    <main className="fatal other-tab" aria-labelledby="other-tab-title">
      <h1 id="other-tab-title">{moved ? t.movedTitle : t.title}</h1>
      <p>{moved ? t.movedBody : t.body}</p>
      <div className="fatal-actions">
        <Button variant="primary" onClick={useHere} disabled={asked} aria-live="polite">
          {asked ? t.opening : t.useHere}
        </Button>
      </div>
    </main>
  );
}
